import type { JsonSchema, JsonSchemaProperty } from '../types/mcp';

export interface AutoPopulateOptions {
  context: Record<string, unknown>;
  existing: Record<string, unknown>;
  touchedPaths: Set<string>;
}

export interface AutoPopulateResult {
  values: Record<string, unknown>;
  suggestions: Map<string, unknown>;
}

type PathToken = string | number;

const normalizeKey = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '');

const isObjectValue = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const isArrayValue = (value: unknown): value is unknown[] => Array.isArray(value);

const isEmptyValue = (value: unknown): boolean => value === null || value === undefined || value === '';

const parsePath = (path: string): PathToken[] => {
  const matches = path.match(/[^.[\]]+/g);
  if (!matches) return [];
  return matches.map(token => (/^\d+$/.test(token) ? Number(token) : token));
};

export const deepClone = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(item => deepClone(item));
  }
  if (isObjectValue(value)) {
    const entries = Object.entries(value).map(([key, val]) => [key, deepClone(val)]);
    return Object.fromEntries(entries);
  }
  return value;
};

export const getValueAtPath = (value: Record<string, unknown>, path: string): unknown => {
  const tokens = parsePath(path);
  let current: unknown = value;
  for (const token of tokens) {
    if (typeof token === 'number') {
      if (!isArrayValue(current)) return undefined;
      current = current[token];
    } else {
      if (!isObjectValue(current)) return undefined;
      current = current[token];
    }
  }
  return current;
};

export const setValueAtPath = (
  value: Record<string, unknown>,
  path: string,
  nextValue: unknown
): Record<string, unknown> => {
  const tokens = parsePath(path);
  if (tokens.length === 0) return value;
  let current: Record<string, unknown> | unknown[] = value;
  tokens.forEach((token, index) => {
    const isLast = index === tokens.length - 1;
    if (isLast) {
      if (typeof token === 'number' && Array.isArray(current)) {
        current[token] = nextValue;
      } else if (typeof token === 'string' && isObjectValue(current)) {
        current[token] = nextValue;
      }
      return;
    }
    const nextToken = tokens[index + 1];
    if (typeof token === 'number') {
      if (!Array.isArray(current)) return;
      const existing = current[token];
      if (existing === undefined) {
        current[token] = typeof nextToken === 'number' ? [] : {};
      }
      current = current[token] as Record<string, unknown> | unknown[];
      return;
    }
    if (typeof token === 'string') {
      if (!isObjectValue(current)) return;
      const existing = current[token];
      if (existing === undefined) {
        current[token] = typeof nextToken === 'number' ? [] : {};
      }
      current = current[token] as Record<string, unknown> | unknown[];
    }
  });
  return value;
};

const getDefaultValueInternal = (schema: JsonSchemaProperty): unknown => {
  if (schema.default !== undefined) return schema.default;
  if (schema.type === 'object' && schema.properties) {
    const result: Record<string, unknown> = {};
    for (const [key, propSchema] of Object.entries(schema.properties)) {
      const childDefault = getDefaultValueInternal(propSchema);
      if (childDefault !== undefined) {
        result[key] = childDefault;
      }
    }
    return Object.keys(result).length > 0 ? result : undefined;
  }
  if (schema.type === 'array' && schema.items) {
    if (schema.default !== undefined) return schema.default;
    return [];
  }
  if (schema.type === 'boolean') return false;
  return undefined;
};

export const getDefaultValue = (schema: JsonSchemaProperty): unknown => {
  return getDefaultValueInternal(schema);
};

const isCompatibleType = (schema: JsonSchemaProperty, value: unknown): boolean => {
  if (schema.type === 'string') return typeof value === 'string';
  if (schema.type === 'number' || schema.type === 'integer') {
    return typeof value === 'number' || (typeof value === 'string' && value.trim() !== '');
  }
  if (schema.type === 'boolean') return typeof value === 'boolean' || value === 'true' || value === 'false';
  if (schema.type === 'array') return Array.isArray(value);
  if (schema.type === 'object') return isObjectValue(value);
  return false;
};

const coerceValue = (schema: JsonSchemaProperty, value: unknown): unknown => {
  if (schema.type === 'number' || schema.type === 'integer') {
    const next = typeof value === 'number' ? value : Number(value);
    return Number.isNaN(next) ? value : next;
  }
  if (schema.type === 'boolean') {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return value;
  }
  return value;
};

const buildContextLookup = (context: Record<string, unknown>): Map<string, unknown> => {
  const lookup = new Map<string, unknown>();
  Object.entries(context).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    const normalized = normalizeKey(key);
    lookup.set(normalized, value);
    if (normalized.endsWith('id')) {
      lookup.set(normalized.replace(/id$/, ''), value);
    }
  });
  return lookup;
};

const findContextValue = (
  name: string,
  schema: JsonSchemaProperty,
  context: Map<string, unknown>
): unknown => {
  const normalized = normalizeKey(name);
  const candidates = [normalized];
  if (normalized.endsWith('id')) {
    candidates.push(normalized.replace(/id$/, ''));
  }
  if (normalized.startsWith('id')) {
    candidates.push(normalized.replace(/^id/, ''));
  }
  for (const candidate of candidates) {
    if (context.has(candidate)) {
      const value = context.get(candidate);
      if (value !== undefined && isCompatibleType(schema, value)) {
        return coerceValue(schema, value);
      }
    }
  }
  return undefined;
};

export const autoPopulateParameters = (
  schema: JsonSchema,
  options: AutoPopulateOptions
): AutoPopulateResult => {
  const values = deepClone(options.existing) as Record<string, unknown>;
  const suggestions = new Map<string, unknown>();
  const contextLookup = buildContextLookup(options.context);

  const applyToSchema = (
    currentSchema: JsonSchemaProperty,
    basePath: string,
    propertyName: string
  ) => {
    const path = basePath ? `${basePath}.${propertyName}` : propertyName;
    const touched = options.touchedPaths.has(path);
    const existingValue = getValueAtPath(values, path);

    if (!touched && isEmptyValue(existingValue)) {
      const contextValue = findContextValue(propertyName, currentSchema, contextLookup);
      if (contextValue !== undefined) {
        setValueAtPath(values, path, contextValue);
        suggestions.set(path, contextValue);
      } else {
        const defaultValue = getDefaultValueInternal(currentSchema);
        if (defaultValue !== undefined) {
          setValueAtPath(values, path, defaultValue);
          suggestions.set(path, defaultValue);
        }
      }
    }

    const nextValue = getValueAtPath(values, path);
    if (currentSchema.type === 'object' && currentSchema.properties) {
      const container = isObjectValue(nextValue) ? nextValue : {};
      setValueAtPath(values, path, container);
      Object.entries(currentSchema.properties).forEach(([childName, childSchema]) => {
        applyToSchema(childSchema, path, childName);
      });
    }
  };

  Object.entries(schema.properties || {}).forEach(([propertyName, propertySchema]) => {
    applyToSchema(propertySchema, '', propertyName);
  });

  return { values, suggestions };
};

export const validateParameters = (
  schema: JsonSchema,
  values: Record<string, unknown>
): Map<string, string> => {
  const errors = new Map<string, string>();

  const validateSchema = (
    currentSchema: JsonSchemaProperty,
    value: unknown,
    path: string
  ): void => {
    if (isEmptyValue(value)) {
      return;
    }

    if (currentSchema.type === 'string' && currentSchema.enum && !currentSchema.enum.includes(value)) {
      errors.set(path, 'Value must be one of the allowed options');
    }

    if (currentSchema.type === 'number' || currentSchema.type === 'integer') {
      const numericValue = typeof value === 'number' ? value : Number(value);
      if (Number.isNaN(numericValue)) {
        errors.set(path, 'Must be a valid number');
        return;
      }
      if (currentSchema.minimum !== undefined && numericValue < currentSchema.minimum) {
        errors.set(path, `Must be at least ${currentSchema.minimum}`);
      }
      if (currentSchema.maximum !== undefined && numericValue > currentSchema.maximum) {
        errors.set(path, `Must be at most ${currentSchema.maximum}`);
      }
    }

    if (currentSchema.type === 'boolean' && typeof value !== 'boolean') {
      errors.set(path, 'Must be a boolean value');
    }

    if (currentSchema.type === 'object') {
      if (!isObjectValue(value)) {
        errors.set(path, 'Must be an object');
        return;
      }
      const required = currentSchema.required || [];
      required.forEach(requiredKey => {
        const requiredPath = path ? `${path}.${requiredKey}` : requiredKey;
        const requiredValue = getValueAtPath(value as Record<string, unknown>, requiredKey);
        if (isEmptyValue(requiredValue)) {
          errors.set(requiredPath, 'This parameter is required');
        }
      });
      Object.entries(currentSchema.properties || {}).forEach(([key, childSchema]) => {
        const childPath = path ? `${path}.${key}` : key;
        const childValue = (value as Record<string, unknown>)[key];
        validateSchema(childSchema, childValue, childPath);
      });
    }

    if (currentSchema.type === 'array') {
      if (!Array.isArray(value)) {
        errors.set(path, 'Must be an array');
        return;
      }
      if (currentSchema.items) {
        value.forEach((item, index) => {
          validateSchema(currentSchema.items as JsonSchemaProperty, item, `${path}[${index}]`);
        });
      }
    }
  };

  Object.entries(schema.properties || {}).forEach(([key, propertySchema]) => {
    const value = values[key];
    if ((schema.required || []).includes(key) && isEmptyValue(value)) {
      errors.set(key, 'This parameter is required');
      return;
    }
    validateSchema(propertySchema, value, key);
  });

  return errors;
};
